import {Key} from 'react';
import api from '../../utils/api';
import {ParamsType} from '../../utils';
import {
    CompletionStatusFilter,
    WellEntity,
    WellEntityToUpdate,
    WellEntityToUpdateWithoutId,
    WellsTreeField,
} from '@features/wells/types';
import {GetFieldsResponse, PadWithWells} from '@features/wells/fields/types';
import {WellsQuantityPad} from '@features/wells/wellQuantity/types';

export class WellsService {
    private static instance: WellsService;

    public static getInstance() {
        if (!WellsService.instance) {
            WellsService.instance = new WellsService();
        }

        return WellsService.instance;
    }

    private constructor() {}

    public getWells(params: ParamsType): Promise<WellEntity[]> {
        return api.get(`/wells`, params);
    }

    public getWellById(id: number | string | undefined): Promise<WellEntity> {
        return api.get<WellEntity>(`/wells/${id}`).then((result) => {
            return result;
        });
    }

    public createWell(data: WellEntityToUpdateWithoutId): Promise<void> {
        return api.post(`/wells`, data);
    }

    public createWellInPad(
        padId: string | number,
        data: WellEntityToUpdateWithoutId,
    ): Promise<unknown> {
        return api.post(
            `/wells`,
            data,
            {},
            {
                padId,
            },
        );
    }

    public updateWell(data: WellEntityToUpdate): Promise<void> {
        return api.put(`/wells/${data.id}`, data);
    }

    public deleteWell(id: string | number): Promise<void> {
        return api.delete(`/wells/${id}`);
    }

    public bulkDeleteWells(ids: Key[]): Promise<void> {
        return api.delete(`/wells`, null, {
            params: {
                id: ids,
            },
        });
    }

    public getWellsByField(field: string): Promise<WellEntity[]> {
        return api.get('/wells/titlesByField', {
            field,
        });
    }

    public getTablesToCopy(): Promise<string[]> {
        return api.get(`/wells/allowed-tables-to-copy`);
    }

    public loadWellFields() {
        return api.get<GetFieldsResponse>('/well-fields').then((result) =>
            result.map((field) => ({
                ...field,
                pads: field?.pads?.map((pad) => ({
                    ...pad,
                    fieldId: field.id,
                })),
            })),
        );
    }

    public getWellsTree(): Promise<WellsTreeField[]> {
        return api.get(`/wells/tree`);
    }

    public findWithExtendedStatus({
        wellprofile,
        status,
        search,
        completionStatusFilter,
    }: {
        wellprofile: string[];
        status: string[];
        search: string | null;
        completionStatusFilter?: CompletionStatusFilter;
    }) {
        return api.get<WellEntity[]>('/wells/tree/search', {
            wellprofile: wellprofile,
            status: status,
            search,
            completionStatusFilter,
        });
    }

    public loadWellPads(fieldId: string | number) {
        return api.get<PadWithWells[]>(
            '/well-pads/find-by-field',
            {
                fieldId,
            },
            {
                withoutGlobalLoader: true,
            },
        );
    }

    public getMonitoringWellsTree(): Promise<WellsTreeField[]> {
        return api.get(`/monitoring/wells/tree`);
    }

    public searchMonitoringWells({
        wellprofile,
        status,
        search,
        onlyWithConnections,
    }: {
        wellprofile: string[];
        status: string[];
        search: string;
        onlyWithConnections: boolean;
    }) {
        return api.get<WellEntity[]>('/monitoring/wells', {
            wellprofile: wellprofile,
            status: status,
            search,
            onlyWithConnections,
        });
    }

    public getWellsQuantityData({type, years}: {type: 'status' | 'type'; years: string[]}) {
        return api.get<WellsQuantityPad[]>(`/wells/counts`, {
            type: type.toUpperCase(),
            year: years,
        });
    }
}
